import { Megaphone } from 'lucide-react';
import { Button } from '@heroui/react';
import type {
  AiBulkRequestCard,
  AiMisuseCard,
  AiOwnedSpotCard,
  AiWindowCard,
} from '../../api/ai';
import { getUsageTypeLabel } from '../my-parking-spots/utils/usage-display';
import {
  ASSISTANT_CANCEL_BULK_LABEL,
  ASSISTANT_CANCEL_WINDOW_LABEL,
  ASSISTANT_LEND_SPOT_LABEL,
  ASSISTANT_REPORT_MISUSE_HINT,
  ASSISTANT_REPORT_MISUSE_LABEL,
  ASSISTANT_RESOLVE_ALREADY_LABEL,
  ASSISTANT_RESOLVE_WILL_REMOVE_LABEL,
} from './assistant-constants';
import { formatChatPeriod } from './assistant-spot-format';

function cardShell(embedded: boolean): string {
  return `rounded-2xl border border-separator px-3 py-2.5 ${
    embedded ? 'bg-surface' : 'bg-surface shadow-sm'
  }`;
}

function listClass(embedded: boolean): string {
  return `flex flex-col gap-2 ${embedded ? 'w-full' : 'w-full max-w-[90%] self-start'}`;
}

function placeLabel(location: string | null, level: string | null): string {
  return [level, location].filter(Boolean).join(' · ');
}

interface AssistantOwnedSpotCardsProps {
  spots: AiOwnedSpotCard[];
  onLend?: (spot: AiOwnedSpotCard) => void;
  embedded?: boolean;
}

export function AssistantOwnedSpotCards({
  spots,
  onLend,
  embedded = false,
}: AssistantOwnedSpotCardsProps) {
  if (spots.length === 0) return null;

  return (
    <div className={listClass(embedded)}>
      {spots.map((spot) => {
        const place = placeLabel(spot.location, spot.level);
        return (
          <article key={spot.id} className={cardShell(embedded)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-tight tracking-tight">
                  Vaga {spot.spotCode}
                </p>
                {place ? <p className="mt-0.5 truncate text-xs text-muted">{place}</p> : null}
              </div>
              <span className="shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-medium text-accent">
                Sua vaga
              </span>
            </div>
            {spot.canLend && onLend ? (
              <Button
                size="sm"
                variant="primary"
                className="mt-2.5 h-8 w-full"
                onPress={() => onLend(spot)}
              >
                {ASSISTANT_LEND_SPOT_LABEL}
              </Button>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

interface AssistantWindowCardsProps {
  windows: AiWindowCard[];
  onCancel?: (windowId: string) => void;
  canCancel?: (window: AiWindowCard) => boolean;
  embedded?: boolean;
}

export function AssistantWindowCards({
  windows,
  onCancel,
  canCancel,
  embedded = false,
}: AssistantWindowCardsProps) {
  if (windows.length === 0) return null;

  return (
    <div className={listClass(embedded)}>
      {windows.map((window) => {
        const showCancel =
          onCancel != null && (canCancel ? canCancel(window) : window.canCancel);
        const kind =
          window.allowRent && !window.allowBorrow
            ? 'Aluguel'
            : window.allowBorrow && window.allowRent
              ? 'Empréstimo e aluguel'
              : 'Empréstimo';
        return (
          <article key={window.id} className={cardShell(embedded)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-tight tracking-tight">
                  Vaga {window.spotCode}
                </p>
                <p className="mt-0.5 text-xs text-muted">{kind}</p>
              </div>
            </div>
            <p className="mt-2 text-[13px] font-medium tabular-nums leading-snug text-foreground">
              {formatChatPeriod(window.startAt, window.endAt)}
            </p>
            {showCancel ? (
              <Button
                size="sm"
                variant="secondary"
                className="mt-2.5 h-8 w-full"
                onPress={() => onCancel?.(window.id)}
              >
                {ASSISTANT_CANCEL_WINDOW_LABEL}
              </Button>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

interface AssistantBulkRequestCardsProps {
  requests: AiBulkRequestCard[];
  onCancel?: (requestId: string) => void;
  canCancel?: (request: AiBulkRequestCard) => boolean;
  embedded?: boolean;
}

export function AssistantBulkRequestCards({
  requests,
  onCancel,
  canCancel,
  embedded = false,
}: AssistantBulkRequestCardsProps) {
  if (requests.length === 0) return null;

  return (
    <div className={listClass(embedded)}>
      {requests.map((request) => {
        const showCancel =
          onCancel != null && (canCancel ? canCancel(request) : request.canCancel);
        return (
          <article key={request.id} className={cardShell(embedded)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-tight tracking-tight">
                  Solicitação em massa
                </p>
                <p className="mt-0.5 text-xs text-muted">{getUsageTypeLabel(request.type)}</p>
              </div>
            </div>
            <p className="mt-2 text-[13px] font-medium tabular-nums leading-snug text-foreground">
              {formatChatPeriod(request.startAt, request.endAt)}
            </p>
            {showCancel ? (
              <Button
                size="sm"
                variant="secondary"
                className="mt-2.5 h-8 w-full"
                onPress={() => onCancel?.(request.id)}
              >
                {ASSISTANT_CANCEL_BULK_LABEL}
              </Button>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

interface AssistantMisuseCardsProps {
  reports: AiMisuseCard[];
  onWillRemove?: (report: AiMisuseCard) => void;
  onAlreadyResolved?: (report: AiMisuseCard) => void;
  canResolve?: (report: AiMisuseCard) => boolean;
  embedded?: boolean;
}

export function AssistantMisuseCards({
  reports,
  onWillRemove,
  onAlreadyResolved,
  canResolve,
  embedded = false,
}: AssistantMisuseCardsProps) {
  if (reports.length === 0) return null;

  return (
    <div className={listClass(embedded)}>
      {reports.map((report) => {
        const showResolve =
          (onWillRemove != null || onAlreadyResolved != null) &&
          (canResolve ? canResolve(report) : report.canResolve);
        return (
          <article key={report.id} className={cardShell(embedded)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-tight tracking-tight">
                  Vaga {report.spotCode}
                </p>
                <p className="mt-0.5 text-xs text-muted">Placa {report.licensePlate}</p>
              </div>
              <span className="shrink-0 rounded-full bg-danger/15 px-2 py-0.5 text-[11px] font-medium text-danger">
                Mau uso
              </span>
            </div>
            {showResolve ? (
              <div className="mt-2.5 flex flex-col gap-2">
                {onWillRemove ? (
                  <Button
                    size="sm"
                    variant="primary"
                    className="h-8 w-full"
                    onPress={() => onWillRemove(report)}
                  >
                    {ASSISTANT_RESOLVE_WILL_REMOVE_LABEL}
                  </Button>
                ) : null}
                {onAlreadyResolved ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-full"
                    onPress={() => onAlreadyResolved(report)}
                  >
                    {ASSISTANT_RESOLVE_ALREADY_LABEL}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

export function AssistantReportMisuseCta({ onPress }: { onPress: () => void }) {
  return (
    <div className="flex flex-col gap-2 border-t border-separator pt-2.5">
      <p className="text-sm text-muted">{ASSISTANT_REPORT_MISUSE_HINT}</p>
      <Button size="sm" variant="primary" className="h-8 w-full" onPress={onPress}>
        <Megaphone size={14} />
        {ASSISTANT_REPORT_MISUSE_LABEL}
      </Button>
    </div>
  );
}
